/**
 * The end-of-effort transaction.
 *
 * This module owns the one write order the whole product rests on: the terminal
 * snapshot is durable *before* history is touched, history is written next, and
 * the checkpoint is cleared last. Ordered that way, every failure leaves the
 * user's work on the device rather than between two stores — which is the only
 * property that lets `endEffort` be retried at all.
 *
 * Nothing here reads a clock. The instant the effort ended is frozen by the
 * caller into {@link ArchivedTermination} and is re-used verbatim on every
 * retry, so a session archived on the third attempt does not claim the user
 * trained until then (F-TR-38).
 */

import type {
  ArchivedTermination,
  DiscardedEffort,
  DiscardedTermination,
  EffortCheckpoint,
  EffortEndResult,
  EffortSessionDraft,
  FailClosedTrigger,
  WriteOutcome,
} from "@ff/domain";
import type {
  CheckpointStorePort,
  EffortCheckpointKind,
  EffortStorePort,
} from "@ff/domain/ports";
import { EFFORT_FAILURE_COPY, discardEffort, sealEffortSession } from "@ff/domain";

/** The two stores the transaction spans. */
export interface EffortArchiveDependencies {
  readonly effortStore: EffortStorePort;
  readonly checkpointStore: CheckpointStorePort;
}

export interface EndEffortRequest {
  readonly termination: ArchivedTermination;
  readonly draft: EffortSessionDraft;
  /**
   * The snapshot as it stands at the moment of ending, carrying
   * `terminalOutcome`. Its presence in the store is the signal that an
   * idempotent retry is owed, so it is written before history is attempted and
   * only removed once history has confirmed the record.
   */
  readonly terminalCheckpoint: EffortCheckpoint;
}

/**
 * Why the checkpoint survived a completed archive.
 *
 * Reported rather than thrown: history is confirmed at this point, so refusing
 * to show the user their session would be a lie in the other direction. The
 * stale terminal checkpoint is picked up by {@link pendingArchiveRetry} on the
 * next launch and cleared then.
 */
export interface ArchiveCheckpointResidue {
  readonly outcome: WriteOutcome;
}

export type EndEffortResult = EffortEndResult & {
  /** Present only when history is confirmed but the checkpoint refused to clear. */
  readonly checkpointResidue?: ArchiveCheckpointResidue;
};

/**
 * Ends a running effort and puts it in History.
 *
 * Idempotent by construction: the sealed session and its execution-entry ids are
 * derived from the draft and the frozen termination, so a second call with the
 * same arguments produces byte-identical rows. The store answers
 * `alreadyArchived` for the retry that arrives after a write the caller never
 * saw confirmed, and this function treats that exactly like a first success.
 */
export async function endEffort(
  request: EndEffortRequest,
  dependencies: EffortArchiveDependencies,
): Promise<EndEffortResult> {
  const { effortStore, checkpointStore } = dependencies;
  const { termination, draft, terminalCheckpoint } = request;

  // Step one: make the terminal snapshot durable. If this fails there is nothing
  // to roll back — the previous checkpoint is still on disk and still describes
  // a session the user can resume — so the caller is told to retry rather than
  // being handed a record that only exists in memory.
  const snapshot = await checkpointStore.save(terminalCheckpoint, "immediate");
  if (snapshot.status !== "verified") {
    return { kind: "retryPending", termination, draft };
  }

  const session = sealEffortSession(termination, draft);
  const archived = await effortStore.archive(session, termination);

  if (archived.kind === "notConfirmed" || archived.kind === "unavailable") {
    // The snapshot written above is the retry's input. Nothing is lost and
    // nothing half-written is left claiming to be history.
    return { kind: "retryPending", termination, draft };
  }

  // Step three, and only now: history is confirmed, so the checkpoint may go.
  const cleared = await checkpointStore.clear(terminalCheckpoint.shape);
  if (cleared.status !== "verified") {
    return {
      kind: "archived",
      session: archived.session,
      checkpointResidue: { outcome: cleared },
    };
  }

  return { kind: "archived", session: archived.session };
}

/**
 * Ends a running effort without recording it.
 *
 * The return type is {@link DiscardedEffort}, which carries no outcome and no
 * durations and which no function maps to an {@link ArchivedTermination}. That
 * is what makes "a discarded workout writes no History entry" a property of the
 * types rather than a rule this function has to remember.
 */
export async function discardActiveEffort(
  termination: DiscardedTermination,
  draft: Pick<EffortSessionDraft, "activity" | "startedAt">,
  kind: EffortCheckpointKind,
  dependencies: Pick<EffortArchiveDependencies, "checkpointStore">,
): Promise<
  | { readonly kind: "discarded"; readonly discarded: DiscardedEffort }
  | { readonly kind: "discardFailed"; readonly outcome: WriteOutcome }
> {
  const cleared = await dependencies.checkpointStore.clear(kind);
  if (cleared.status !== "verified") {
    // Fail closed: the effort is still on the device. Reporting success here
    // would leave a checkpoint that resurrects a session the user chose to throw
    // away, which is worse than telling them the discard did not take.
    return { kind: "discardFailed", outcome: cleared };
  }
  return {
    kind: "discarded",
    discarded: discardEffort({
      sessionId: termination.sessionId,
      activity: draft.activity,
      startedAt: draft.startedAt,
      discardedAt: termination.discardedAt,
    }),
  };
}

/* ------------------------------------------------------------------------- *
 * Start preflight (F-TR-39)
 * ------------------------------------------------------------------------- */

export type StartPreflight =
  | { readonly kind: "clear" }
  | {
      readonly kind: "refused";
      readonly reason: FailClosedTrigger;
      /** Exactly the copy the spec fixes for this refusal. */
      readonly message: string;
    };

/**
 * Decides whether a new effort may begin at all.
 *
 * Canonical history is read first, and an unreadable read refuses the start.
 * This is not caution about the *new* session — it is that a session which could
 * never be archived afterwards is a session the app would promise to keep and
 * then lose. The refusal is deliberately distinct from the unreadable-checkpoint
 * confirmation, which is about an *old* effort and offers the user a choice.
 */
export async function startPreflight(
  effortStore: Pick<EffortStorePort, "readSessions">,
): Promise<StartPreflight> {
  const history = await effortStore.readSessions();
  if (history.status === "unreadable") {
    return {
      kind: "refused",
      reason: history.trigger,
      message: EFFORT_FAILURE_COPY.startPreflight,
    };
  }
  return { kind: "clear" };
}
